
'use client';

/**
 * Global Providers wrapper for the entire app.  This mounts **once** at the
 * root level so that stateful providers (Redux, Auth, Sentry, etc.) are not
 * recreated on every navigation.
 *
 * NOTE:  Route-group layouts can still add **feature-specific** providers
 * (e.g. dashboard Sidebar state, Socket connections) but MUST NOT duplicate
 * anything declared here.
 */

import React, { ReactNode } from 'react';

import { AppProvider } from '@/core/providers/AppProvider';
import { UIProvider } from '@/core/providers/UIProvider';
import { CsrfProvider } from '@/core/providers/CsrfProvider';
import { AppInitializationProvider } from '@/core/providers/AppInitializationProvider';
import { NprogressProvider } from '@/core/providers/nprogress';

// Centralized session status overlay (expired / replaced)
import { SessionStatusOverlay } from '@/core/auth/components/SessionStatusOverlay';

import { ClientInit } from './client-init';
import { Toaster as SonnerToaster } from 'sonner';
import { RootErrorBoundary } from './error-boundary';

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <RootErrorBoundary>
      <AppProvider>
        {/* CSRF token fetched once at app initialization */}
        <CsrfProvider>
          {/* Track when all systems are ready */}
          <AppInitializationProvider>
              {/* Client-only helpers / logging initialisation */}
              <ClientInit />
              {/* Sonner toaster - modern, beautiful notifications */}
              <SonnerToaster
                position="top-right"
                richColors
                expand={true}
                visibleToasts={3}
                toastOptions={{
                  className: 'voley-toast',
                  style: {
                    background: 'var(--color-card-bg, #ffffff)',
                    border: '1px solid var(--color-border, #D5D9DF)',
                    color: 'var(--color-text-primary, #374151)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.04), 0 10px 24px rgba(0, 0, 0, 0.12), 0 20px 48px rgba(0, 0, 0, 0.08)',
                    fontFamily: 'var(--font-body)',
                  },
                }}
              />

              {/* Navigation progress indicator - shows bar at top during navigation */}
              <NprogressProvider />

              {/* Centralized session expired/replaced overlay */}
              <SessionStatusOverlay />

              {/* UI preferences (theme, font-size, sidebar) */}
              <UIProvider>{children}</UIProvider>
          </AppInitializationProvider>
        </CsrfProvider>
      </AppProvider>
    </RootErrorBoundary>
  );
}
